import { Badge, Detail } from '../../components/ui'
import type { SearchField, SearchRequest } from '../../api/threads'
import { t } from '../../i18n'
import { fieldValue, longDate, money, months, percent } from '../../format'
import {
  billsScopeLabel,
  budgetScopeLabel,
  confidenceBand,
  confidenceLabel,
  fieldLabel,
  fieldOrder,
  fieldSourceLabel,
  importanceLabel,
  riskFlagLabel,
  searchStatusLabel,
  termBandLabel,
  unitTypesLabel,
} from '../../labels'

/**
 * What the system believes the client is looking for, and why it believes it.
 *
 * The summary on top is what a manager reads; the list under it is how they
 * check it. Every value in the list carries how sure the system is, who said
 * it and a link to the message it came from: a manager who cannot trace a
 * number back to the sentence that produced it cannot correct it, and a value
 * nobody can correct is a value the matcher will keep using.
 */
export function SearchRequestPanel({
  request,
  onEvidence,
}: {
  readonly request: SearchRequest | undefined
  readonly onEvidence: (messageId: string) => void
}) {
  if (request === undefined) {
    return (
      <section className="panel-block">
        <h2 className="panel-block__title">{t('search.title')}</h2>
        <p className="panel-block__empty">{t('search.none')}</p>
        <p className="panel-block__hint">{t('search.noneHint')}</p>
      </section>
    )
  }

  const duration = months(request.durationMonthsHundredths)
  const fields = [...request.fields].sort((a, b) => fieldOrder(a.field) - fieldOrder(b.field))

  return (
    <section className="panel-block">
      <div className="panel-block__head">
        <h2 className="panel-block__title">{t('search.title')}</h2>
        <span className="panel-block__aside">
          <Badge tone="neutral">{searchStatusLabel(request.status)}</Badge>
          <span className="panel-block__version">{t('search.version', { version: request.version })}</span>
        </span>
      </div>

      <dl className="details">
        <Detail term={t('search.completeness')}>
          <Meter fraction={request.completeness} />
        </Detail>
        <Detail term={t('search.unitType')}>
          {unitTypesLabel(request.unitType)}
          {request.preferredUnitType === undefined ? null : (
            <span className="detail__note">
              {t('search.preferred', { unit: unitTypesLabel(request.preferredUnitType) })}
            </span>
          )}
        </Detail>
        <Detail term={t('search.people')}>{peopleSummary(request)}</Detail>
        <Detail term={t('search.budget')}>
          <Budget request={request} />
        </Detail>
        <Detail term={t('search.dates')}>{datesSummary(request)}</Detail>
        <Detail term={t('search.term')}>
          {/*
            The band and the duration are shown side by side because they are not
            the same statement: the band is what the code computed from the dates
            (invariant 4), and it is never the legal contract type.
          */}
          {duration ?? t('search.termUnknown')}
          {request.internalTermBand === '' ? null : (
            <span className="detail__note">{termBandLabel(request.internalTermBand)}</span>
          )}
        </Detail>
      </dl>

      {request.missingRequired.length > 0 ? (
        <div className="chips">
          <span className="chips__label">{t('search.missing')}</span>
          {request.missingRequired.map((code) => (
            <Badge key={code} tone="warning">
              {fieldLabel(code)}
            </Badge>
          ))}
        </div>
      ) : null}

      {request.riskFlags.length > 0 ? (
        <div className="chips">
          <span className="chips__label">{t('search.risks')}</span>
          {request.riskFlags.map((code) => (
            <Badge key={code} tone="danger">
              {riskFlagLabel(code)}
            </Badge>
          ))}
        </div>
      ) : null}

      <h3 className="panel-block__subtitle">{t('search.fields')}</h3>
      {fields.length === 0 ? (
        <p className="panel-block__empty">{t('search.fieldsEmpty')}</p>
      ) : (
        <>
          <p className="panel-block__hint">{t('search.fieldsHint')}</p>
          <ul className="fields">
            {fields.map((field) => (
              <FieldRow key={`${field.field}:${field.createdAt}`} field={field} onEvidence={onEvidence} />
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

/** "2 чел. + ещё 1 возможно", or that nobody has said. */
function peopleSummary(request: SearchRequest): string {
  if (request.peopleConfirmed <= 0 && request.peoplePossible <= 0) return t('search.notSaid')
  if (request.peoplePossible <= 0) return t('search.peopleOnly', { confirmed: request.peopleConfirmed })
  return t('search.peopleWithPossible', {
    confirmed: request.peopleConfirmed,
    possible: request.peoplePossible,
  })
}

/**
 * The dates as the client gave them. Read from the field statements when there
 * is one, because they carry the precision: «с сентября 2026» was said, «с
 * 1 сентября 2026» was not, and the request columns store both as the first.
 */
function datesSummary(request: SearchRequest): string {
  const said = (name: string) => request.fields.find((field) => field.field === name)
  const parts: string[] = []

  const start = said('start_date')
  if (start !== undefined) {
    parts.push(t('search.datesFrom', { date: fieldValue(start.value, start.field) }))
  } else if (request.startDateTarget !== undefined) {
    parts.push(t('search.datesFrom', { date: longDate(request.startDateTarget) }))
  } else if (request.startDateEarliest !== undefined && request.startDateLatest !== undefined) {
    parts.push(
      t('search.datesWindow', { from: longDate(request.startDateEarliest), to: longDate(request.startDateLatest) }),
    )
  } else if (request.startDateEarliest !== undefined) {
    parts.push(t('search.datesFrom', { date: longDate(request.startDateEarliest) }))
  }

  const end = said('end_date')
  if (end !== undefined) {
    parts.push(t('search.datesTo', { date: fieldValue(end.value, end.field) }))
  } else if ((request.endDateTarget ?? request.endDateLatest) !== undefined) {
    parts.push(t('search.datesTo', { date: longDate(request.endDateTarget ?? request.endDateLatest) }))
  }

  return parts.length === 0 ? t('search.datesNone') : parts.join(' ')
}

/** The two budget figures and what they cover, or that none was named. */
function Budget({ request }: { readonly request: SearchRequest }) {
  if (request.budgetIdeal === undefined && request.budgetMax === undefined) {
    return <>{t('search.budgetNone')}</>
  }

  const covers = [
    request.budgetScope === '' ? '' : budgetScopeLabel(request.budgetScope),
    request.billsScope === '' ? '' : billsScopeLabel(request.billsScope),
  ].filter((part) => part !== '')

  return (
    <div className="budget">
      {request.budgetIdeal === undefined ? null : (
        <span>
          {money(request.budgetIdeal)} <span className="budget__note">{t('search.budgetIdeal')}</span>
        </span>
      )}
      {request.budgetMax === undefined ? null : (
        <span>
          {money(request.budgetMax)} <span className="budget__note">{t('search.budgetMax')}</span>
        </span>
      )}
      {covers.length === 0 ? null : <span className="budget__note">{covers.join(' · ')}</span>}
    </div>
  )
}

/**
 * How much of the request is known, as a bar rather than a number.
 *
 * A bar is read at a glance; the number stays next to it because "почти
 * заполнено" is not something anyone should have to eyeball off a rectangle.
 */
function Meter({ fraction }: { readonly fraction: number }) {
  const value = percent(fraction)
  return (
    <span className="meter" role="img" aria-label={`${value}%`}>
      <span className="meter__track">
        <span className="meter__fill" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
      </span>
      <span className="meter__value">{value}%</span>
    </span>
  )
}

function FieldRow({
  field,
  onEvidence,
}: {
  readonly field: SearchField
  readonly onEvidence: (messageId: string) => void
}) {
  const band = confidenceBand(field.confidence)
  const several = field.evidenceMessageIds.length > 1

  return (
    <li className={`field-row field-row--${band}`}>
      <div className="field-row__head">
        <span className="field-row__name">{fieldLabel(field.field)}</span>
        <span
          className={`field-row__confidence field-row__confidence--${band}`}
          title={t('search.confidence', { value: percent(field.confidence) })}
        >
          {confidenceLabel(field.confidence)}
        </span>
      </div>

      <div className="field-row__value">{fieldValue(field.value, field.field)}</div>

      <div className="field-row__meta">
        <span>{fieldSourceLabel(field.source)}</span>
        {field.importance === '' ? null : (
          <span className={field.importance === 'hard' ? 'field-row__hard' : undefined}>
            {importanceLabel(field.importance)}
          </span>
        )}
        {field.confirmedAt === undefined ? null : (
          <span className="field-row__confirmed">{t('search.confirmed')}</span>
        )}
        {field.evidenceMessageIds.length === 0 ? (
          // Said out loud rather than left blank. A value with nothing behind it
          // is the one a manager should look at hardest, and an empty row reads
          // as a row where the evidence simply was not rendered.
          <span className="field-row__noevidence">{t('search.noEvidence')}</span>
        ) : (
          field.evidenceMessageIds.map((messageId, index) => (
            <button
              key={messageId}
              type="button"
              className="evidence"
              onClick={() => onEvidence(messageId)}
              title={t('thread.evidenceFor', { field: fieldLabel(field.field) })}
            >
              {several ? t('search.evidenceNumbered', { n: index + 1 }) : t('search.evidence')}
            </button>
          ))
        )}
      </div>
    </li>
  )
}
